// src/styles/tokens.js
// Единое место для токенов оформления (редизайн дашборда)
import { keyframes } from '@mui/system';

export const PAGE_BG = '#121214';
export const GRID_DOT_COLOR = 'rgba(255,255,255,0.035)';
export const GRID_STEP = 18;
export const PAGE_GLOW = 'rgba(255,112,67,0.10)';

const GRID_DOTS = `radial-gradient(circle at 2px 2px, ${GRID_DOT_COLOR} 1px, transparent 1.5px)`;

// Фон страницы в браузере: тёплое свечение сверху + точечная сетка.
// На мобильных свечение компактнее и начинается от самого верха экрана (под статус-баром),
// на десктопе — шире и гаснет к ~600px.
export const PAGE_BACKGROUND_SX = {
  backgroundColor: PAGE_BG,
  backgroundImage: {
    xs: `radial-gradient(ellipse 520px 420px at 80% 0%, ${PAGE_GLOW}, transparent 70%), ${GRID_DOTS}`,
    md: `radial-gradient(ellipse 900px 520px at 75% -120px, ${PAGE_GLOW}, transparent 70%), ${GRID_DOTS}`,
  },
  backgroundSize: `100% 100%, ${GRID_STEP}px ${GRID_STEP}px`,
  backgroundRepeat: 'no-repeat, repeat',
};

// Карточки
export const CARD_BG_TOP = '#252529';
export const CARD_BG_BOTTOM = '#1c1c1f';
export const CARD_BORDER = '1px solid rgba(255,255,255,0.07)';
export const CARD_HIGHLIGHT = 'inset 0 1px 0 rgba(255,255,255,0.05)';
export const CARD_LABEL_COLOR = '#c7c7cc';
export const CARD_LABEL_SIZE = '14px';
export const CARD_RADIUS = '12px';
export const MAIN_CARD_BG_TOP = '#26262a';

// Поверхность карточки без цветного свечения (диаграммы, таблицы)
export const CARD_SURFACE_SX = {
  background: `linear-gradient(180deg, ${CARD_BG_TOP}, ${CARD_BG_BOTTOM})`,
  border: CARD_BORDER,
  boxShadow: CARD_HIGHLIGHT,
  borderRadius: CARD_RADIUS,
};

// Вторичный текст (шапки таблиц, номера мест)
export const MUTED_TEXT_COLOR = '#8e8e93';
export const DIVIDER_COLOR = 'rgba(255,255,255,0.06)';

// Строка-аккордеон внутри карточки: кнопка во всю ширину, 44px, видимый фокус
export const ACCORDION_BUTTON_SX = {
  width: '100%',
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1,
  borderRadius: '8px',
  textAlign: 'left',
  color: 'rgba(255,255,255,0.9)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
  '&.Mui-focusVisible, &:focus-visible': {
    outline: '2px solid #64b5f6',
    outlineOffset: '2px',
  },
};

// Баннер — рамка и скругление как у карточек
export const BANNER_BORDER = '1px solid rgba(255,255,255,0.08)';

// Шапка и нижняя панель навигации
export const NAV_BG = 'rgba(18,18,20,0.72)';
export const NAV_BG_SOLID = '#141416';
export const NAV_BLUR = 'blur(14px) saturate(140%)';
export const NAV_GRADIENT_LINE =
  'linear-gradient(90deg, rgba(239,83,80,0) 0%, #ef5350 25%, #42a5f5 55%, #ab47bc 80%, rgba(171,71,188,0) 100%)';
export const NAV_GRADIENT_LINE_OPACITY = 0.55;

// Полупрозрачный фон с blur; без поддержки backdrop-filter или в Telegram — непрозрачный
export const navSurfaceSx = (telegram) =>
  telegram
    ? { backgroundColor: NAV_BG_SOLID }
    : {
        backgroundColor: NAV_BG,
        backdropFilter: NAV_BLUR,
        WebkitBackdropFilter: NAV_BLUR,
        '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
          backgroundColor: NAV_BG_SOLID,
        },
      };

// Градиентная линия по краю панели (edge: 'top' | 'bottom')
export const navGradientLineSx = (edge) => ({
  content: '""',
  position: 'absolute',
  left: 0,
  right: 0,
  [edge]: 0,
  height: '1px',
  background: NAV_GRADIENT_LINE,
  opacity: NAV_GRADIENT_LINE_OPACITY,
  pointerEvents: 'none',
});

// Акценты разделов: accent — основной цвет (rgb для прозрачных фонов), tab — цвет текста вкладки
export const SECTION_ACCENTS = {
  '/metrics': { accent: '#ff7043', rgb: '255,112,67', tab: '#ff8a65' },
  '/cost': { accent: '#42a5f5', rgb: '66,165,245', tab: '#64b5f6' },
  '/dashboard': { accent: '#ab47bc', rgb: '171,71,188', tab: '#ce93d8' },
  '/kitchen': { accent: '#26c281', rgb: '38,194,129', tab: '#4dd4a0' },
};

// Места приготовления на графике «Загрузки кухни»: 8 устойчивых цветов (индекс — с бэкенда),
// остальные места — «Прочее» нейтральным цветом
export const KITCHEN_PLACE_COLORS = ['#ff7a45', '#4fa3f7', '#f5c451', '#a78bfa', '#34c38f', '#f06a8a', '#22d3ee', '#e879f9'];
export const KITCHEN_OTHER_COLOR = '#71717a';

// «Настройки» и прочее — прежний нейтральный стиль активной вкладки
export const NEUTRAL_ACTIVE_TAB = { color: '#64b5f6', background: 'rgba(100,181,246,0.12)' };

// Цвета KPI-карточек
export const KPI_COLORS = {
  revenueMain: '#26c6da',
  discountSum: '#f0a43a',
  discountPercent: '#ef6b6b',
  ordersCount: '#66bb6a',
  guestsCount: '#b56ad6',
  averageCheck: '#42a5f5',
  revenuePerGuest: '#26c281',
  costMain: '#ec407a',
  grossProfit: '#66bb6a',
  costPercent: '#f0a43a',
  markupPercent: '#42a5f5',
  costDiscountSum: '#ef6b6b',
};

// Движение. Анимируем изменения данных и переходы, а не появление элементов; показ данных не задерживаем.
// Только transform и opacity (кроме высоты столбцов графиков). prefers-reduced-motion — без переходов.
export const MOTION = {
  fast: 150,
  standard: 220,
  data: 400,
  press: 100,
  contentIn: 200, // появление пришедших данных после скелетона
  dialog: 180, // десктопные диалоги: fade + scale 0.97 → 1
  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
};

export const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

// transition для sx: motionSx(['opacity', 'transform'], MOTION.fast) — с отключением при reduced motion
export const motionSx = (properties, duration = MOTION.standard) => ({
  transition: properties.map((p) => `${p} ${duration}ms ${MOTION.easing}`).join(', '),
  [REDUCED_MOTION]: { transition: 'none' },
});

// Нажатие: scale 0.98 за MOTION.press. Отдельным свойством scale и анимацией, а не transition —
// не перебивает переходы компонентов и складывается с их transform (например, translateY при наведении)
const pressIn = keyframes`
  from { scale: 1; }
  to { scale: 0.98; }
`;
export const PRESS_SX = {
  '&:active:not(.Mui-disabled)': { animation: `${pressIn} ${MOTION.press}ms ${MOTION.easing} forwards` },
  [REDUCED_MOTION]: { '&:active': { animation: 'none' } },
};

// Выпадающие меню: opacity и translateY(-4px) → 0 за MOTION.fast
export const menuIn = keyframes`
  from { transform: translateY(-4px); }
  to { transform: none; }
`;

// Десктопные диалоги: scale(0.97) → 1 вместе с fade (opacity — Fade MUI) за MOTION.dialog
export const dialogIn = keyframes`
  from { transform: scale(0.97); }
  to { transform: none; }
`;

// Смена периода, ресторана или зала: старые данные остаются на месте приглушёнными, пока не придут новые
export const dimSx = (dimmed) => ({
  opacity: dimmed ? 0.5 : 1,
  ...motionSx(['opacity'], MOTION.fast),
});

// Скелетоны загрузки: фон и перелив (единственное бесконечное движение — только пока идёт загрузка)
export const SKELETON_BG = 'rgba(255,255,255,0.05)';
export const SKELETON_WAVE = 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)';
export const SKELETON_WAVE_SECONDS = 1.4;

// "#rrggbb" → "r,g,b" для rgba()
export const hexToRgb = (hex) => {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const num = parseInt(full, 16);
  return `${(num >> 16) & 255},${(num >> 8) & 255},${num & 255}`;
};
